/** Screen chrome and guards shared by every guest route. */
import { ApiError, BottomNav, ButtonLink, Icon, Skeleton, SunsetArcs } from '@pmh/shared';
import { type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEdgeSwipeBack, useBack } from '../lib/nav';
import { useSession } from '../lib/session';
import { useHouseBasics, useStay } from '../lib/queries';
import { RouterLink } from '../routerLink';
import { HelpButton } from './Help';

/** The four tabs (Rook, 2026-10-19, ADR-045). My Stay covers sign-in, registration and its own
 *  pages (Getting Here, ADR-050) too. */
function Tabs() {
  const { pathname } = useLocation();
  const { token, resuming } = useSession();
  const signedOut = !token && !resuming;
  const myStay =
    pathname === '/' || ['/register', '/find-my-stay', '/inactive', '/s/', '/b/', '/my-stay/'].some((p) => pathname.startsWith(p));
  return (
    <BottomNav
      as={RouterLink}
      items={[
        { to: signedOut ? '/find-my-stay' : '/', label: 'My Stay', icon: 'home', active: myStay, highlight: signedOut },
        { to: '/house', label: 'House Guide', icon: 'book', active: pathname.startsWith('/house') },
        { to: '/local', label: 'Local Area', icon: 'pin', active: pathname.startsWith('/local') },
        { to: '/feedback', label: 'Feedback', icon: 'chat', active: pathname.startsWith('/feedback') },
      ]}
    />
  );
}

/** "The Peter MacFarlane Home" → "Peter MacFarlane Home": the top bar is short on room. */
export function shortName(name: string): string {
  return name.replace(/^the\s+/i, '');
}

function Brand() {
  const house = useHouseBasics();
  return (
    <span className="g-appbar__brand">
      <SunsetArcs rings={3} sun width={30} strokeWidth={20} />
      <span className="g-appbar__name">{house ? shortName(house.name) : ''}</span>
    </span>
  );
}

function BackPill({ fallback }: { fallback: string }) {
  const back = useBack(fallback);
  useEdgeSwipeBack(back);
  return (
    <button type="button" className="g-appbar__back" onClick={back}>
      <Icon name="back" size={20} strokeWidth={2.4} />
      Back
    </button>
  );
}

/**
 * The bar at the top of every screen (ADR-050): the house's name, or Back on a page opened from
 * My Stay, and Help on the right, like V1's top bar. Registration screens have their own row
 * (Back and progress) and put the Help button there (`bar="none"`).
 */
export type Bar = 'brand' | 'none' | { back: string };

function AppBar({ bar, tone }: { bar: Exclude<Bar, 'none'>; tone: 'cream' | 'pine' }) {
  return (
    <header className={`g-appbar g-appbar--${tone}`}>
      {bar === 'brand' ? <Brand /> : <BackPill fallback={bar.back} />}
      <HelpButton tone={tone} />
    </header>
  );
}

/** Every guest screen: the top bar, content, and the tabs always at the bottom. */
export function Screen({ children, bar = 'brand', tone = 'cream' }: { children: ReactNode; bar?: Bar; tone?: 'cream' | 'pine' }) {
  return (
    <div className="pmh-screen">
      {bar !== 'none' && <AppBar bar={bar} tone={tone} />}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{children}</main>
      <Tabs />
    </div>
  );
}

export function Loading() {
  return (
    <Screen>
      <div className="pmh-content" aria-busy="true" aria-label="Loading">
        <Skeleton height={48} width="70%" />
        <Skeleton height={180} radius={24} />
        <Skeleton height={60} radius={20} />
      </div>
    </Screen>
  );
}

/**
 * Requires a verified session; a remembered phone is signed back in first (the session does that
 * by itself). With no stay, `house` (when given) is shown from the public house info, so House
 * Guide and Local Area work signed out; otherwise My Stay's sign-in, or the house guide for a
 * phone that chose "just the house info" or whose stay is over (ADR-041).
 */
export function RequireStay({ children, house }: { children: ReactNode; house?: ReactNode }) {
  const { token, houseOnly, resuming } = useSession();
  const stay = useStay();
  if (!token) {
    if (resuming) return <Loading />;
    if (house !== undefined) return <>{house}</>;
    return <Navigate to={houseOnly ? '/house' : '/find-my-stay'} replace />;
  }
  if (stay.error instanceof ApiError && stay.error.status === 410) return <Navigate to="/inactive" replace />;
  return <>{children}</>;
}

/** Shown on a signed-out tab with nothing public to show (Rook turned public content off). */
export function SignInForThis({ what }: { what: string }) {
  const { token } = useSession();
  if (token) return null;
  return (
    <div className="pmh-stack" style={{ gap: 12 }}>
      <p className="pmh-body">Find your stay under My Stay to see {what}.</p>
      <ButtonLink as={RouterLink} to="/find-my-stay" iconRight="arrowRight" block>
        Find my stay
      </ButtonLink>
    </div>
  );
}

/** "Not your stay?": forget this phone's stay and find another (ADR-041). */
export function SwitchStay() {
  const { forget } = useSession();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="g-switch"
      onClick={() => {
        forget();
        navigate('/find-my-stay', { replace: true });
      }}
    >
      Not your stay? Switch
    </button>
  );
}

export function textUsHref(value: string): string | null {
  const v = value.trim();
  if (/^\[.*\]$/.test(v)) return null;
  if (v.includes('@')) return `mailto:${v}`;
  if (/^https?:\/\//.test(v)) return v;
  const digits = v.replace(/[^\d+]/g, '');
  return digits.length >= 7 ? `sms:${digits}` : null;
}
